import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import { formatDate } from '../utils/formatDate';
import CommunityServiceForm from './CommunityServiceForm';

function CommunityService({ juvenileId }) {
  const [offenses, setOffenses] = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const { hasPermission } = useContext(AuthContext);

  const fetchAllData = useCallback(async () => {
    if (!juvenileId) return;
    try {
      setError('');
      const [offensesRes, serviceRes] = await Promise.all([
        apiClient.get(`/api/juveniles/${juvenileId}/offenses`),
        apiClient.get(`/api/case/juveniles/${juvenileId}/community-service`)
      ]);
      // **THE FIX IS HERE:** Extract the array from the response object
      setOffenses(offensesRes.data.offenses || []);
      setServiceRecords(serviceRes.data);
    } catch (err) {
      setError('Failed to fetch community service details.');
      console.error(err);
    }
  }, [juvenileId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleFormSubmit = async (formData) => {
    try {
      if (editingRecord) {
        await apiClient.put(`/api/case/community-service/${editingRecord.id}`, formData);
      } else {
        await apiClient.post(`/api/case/offenses/${formData.offense_id}/community-service`, formData);
      }
      setEditingRecord(null);
      setShowAddForm(false);
      fetchAllData();
    } catch (err) {
      console.error("Failed to save community service", err);
    }
  };

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-slate-800 mb-0">Community Service</h2>
        {hasPermission('assign-community-service') && !showAddForm && !editingRecord && (
          <button onClick={() => setShowAddForm(true)} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
            Assign Service
          </button>
        )}
      </div>

      {showAddForm && <CommunityServiceForm offenses={offenses} onSubmit={handleFormSubmit} onCancel={() => setShowAddForm(false)} />}
      {editingRecord && <CommunityServiceForm offenses={offenses} onSubmit={handleFormSubmit} initialData={editingRecord} onCancel={() => setEditingRecord(null)} />}

      <div className="mt-4 space-y-4">
        {serviceRecords.length > 0 ? (
          serviceRecords.map(record => (
            <div key={record.id} className="p-4 border rounded-lg bg-slate-50">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-bold text-slate-700">Offense: {record.offense_type} (Case: {record.case_number})</h4>
                  <p className={`text-sm font-semibold ${record.status === 'Completed' ? 'text-green-600' : 'text-blue-600'}`}>Status: {record.status}</p>
                </div>
                {hasPermission('assign-community-service') && (
                <button onClick={() => setEditingRecord(record)} className="text-sm text-slate-600 hover:text-gold">Update</button>
              )}
              </div>
              <div className="border-t mt-2 pt-2 text-sm space-y-1">
                <p><strong>Task:</strong> {record.task_description || 'N/A'}</p>
                <p><strong>Hours:</strong> {record.hours_completed || 0} / {record.hours_required}</p>
                <p><strong>Deadline:</strong> {formatDate(record.deadline)}</p>
              </div>
            </div>
          ))
        ) : (
          !showAddForm && !editingRecord && <p>No community service records found.</p>
        )}
      </div>
    </div>
  );
}

export default CommunityService;